import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  output,
} from '@angular/core';

import { translate } from '@jsverse/transloco';

import { AppSharedModule } from '../../../../../app-shared.module';
import { DocumentViewSection } from '../../../../../providers/nlp-output.service';
import { ScalarMetric, VectorMetric } from '../../../../../providers/storage-api/document.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from '../../../../../services/otel.service';
import { SectionFilter } from '../document-view/document-view.component';
import { DocumentScoringMetricsMetricdataSectionComponent } from './document-scoring-metrics-metricdata-section/document-scoring-metrics-metricdata-section.component';
import {
  DocumentScoringMetricsOvertimeSectionComponent,
  DocumentScoringMetricsOvertimeSettings,
} from './document-scoring-metrics-overtime-section/document-scoring-metrics-overtime-section.component';

export interface DocumentScoringPanel {
  type: DocumentScoringPanelType;
  header: string;
  expanded?: boolean;
}

export type DocumentScoringPanelType = 'overtime' | 'scalar_metrics' | 'metadata_metrics';

@EnableClassTelemetry()
@Component({
  selector: 'app-document-scoring-metrics',
  imports: [
    AppSharedModule,
    DocumentScoringMetricsOvertimeSectionComponent,
    DocumentScoringMetricsMetricdataSectionComponent,
  ],
  templateUrl: './document-scoring-metrics.component.html',
  styleUrl: './document-scoring-metrics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DocumentScoringMetricsComponent {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  sections = input<DocumentViewSection[]>();
  sectionFilter = input<SectionFilter>();
  vectorMetrics = input<VectorMetric[]>();
  scalarMetrics = input<ScalarMetric[]>();
  metadata = input<any[]>();
  settings = input<DocumentScoringMetricsOvertimeSettings>();

  selectSection = output<{ section: DocumentViewSection; index: number }>();
  settingsChanged = output<DocumentScoringMetricsOvertimeSettings>();

  showOvertime = computed(
    () => this.sections().some((s) => s.sentimentScore != null) || this.vectorMetrics()?.length
  );
  overtimePanel = computed<DocumentScoringPanel>(() => ({
    ...(this.settings()?.panels?.overtime ?? {}),
    type: 'overtime',
    header: translate('documents.scoring-metrics.overtime-section.header'),
  }));
  simpleScalarMetrics = computed(() =>
    this.scalarMetrics()?.map((m) => ({ name: m.scoreName, value: m.value }))
  );
  scalarMetricsPanel = computed<DocumentScoringPanel>(() => ({
    ...(this.settings()?.panels?.scalar_metrics ?? {}),
    type: 'scalar_metrics',
    header: this.scalarMetricsLabel(),
  }));
  simpleMetadataMetrics = computed(() =>
    this.metadata()?.map((m) => ({ name: m.longLabel, value: m.value, type: m.type }))
  );
  metadataMetricsPanel = computed<DocumentScoringPanel>(() => ({
    ...(this.settings()?.panels?.metadata_metrics ?? {}),
    type: 'metadata_metrics',
    header: this.metadataMetricsLabel(),
  }));

  metadataMetricsLabel = computed(() => {
    const count = this.metadata()?.length;
    if (this.sectionFilter()?.filterType === 'section') {
      return translate('documents.scoring-metrics.section-metadata', {
        count,
      });
    }
    return translate('documents.scoring-metrics.metadata', {
      count,
    });
  });
  scalarMetricsLabel = computed(() => {
    const count = this.scalarMetrics()?.length;
    return translate('documents.scoring-metrics.metrics', {
      count,
    });
  });

  onSectionSelected($event) {
    this.selectSection.emit({
      section: $event,
      index: this.sections().indexOf($event),
    });
  }
  onSettingsChanged(settings: DocumentScoringMetricsOvertimeSettings) {
    this.settingsChanged.emit(settings);
  }

  onExpandedChanged(panel: DocumentScoringPanel) {
    const settingsToUpdate = {
      ...this.settings(),
    };
    const cachedPanel = settingsToUpdate?.panels?.[panel.type];
    if (!cachedPanel) {
      settingsToUpdate.panels = {
        ...(settingsToUpdate.panels ?? {}),
        [panel.type]: panel,
      };
    } else {
      settingsToUpdate.panels[panel.type] = { ...cachedPanel, ...panel };
    }

    // Emit settings changed event
    this.onSettingsChanged(settingsToUpdate);
  }
}
